import React, { useEffect, useState } from 'react';
import { Input, Select, Modal, message, Spin } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { PrivateAxiosInstance } from '../../services/createAxiosInstance';
import { URLs } from '../../constants/apiEndpoints';
import { useDispatch } from 'react-redux';
import { getSessionById } from '../../redux/features/sessions/sessionActions';
import xlsx from 'json-as-xlsx';
import Pagination from '../../components/Pagination/Pagination';

const { Search } = Input;

function Session() {
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [sessionData, setSessionData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const { id } = useParams();
  const dispatch = useDispatch();

  //Search
  const handleSearch = (value) => {
    setSearchText(value);
  };

  const fetachRegiData = async (page = 1, limit = 20) => {
    if (!id) {
      message.error('Session ID not found');
    }
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        search: id,
      }).toString();

      const res = await PrivateAxiosInstance.get(
        `${URLs.GET_REGISTER_SESSION_DETAILS_URL}?${queryParams}`
      );

      const count = res?.data?.events?.length;
      const totalPage = res?.data?.pages;

      const { events } = res?.data || { events: [] };

      if (events) {
        setFilteredData(events);
        setCount(count);
        setTotalPages(totalPage);
      } else {
        message.error('No events data found');
      }
    } catch (err) {
      message.error('Something went wrong during fetching events data');
    } finally {
      setLoading(false);
    }
  };

  const fetachById = async () => {
    try {
      const res = await dispatch(getSessionById({ eventId: id })).unwrap();

      setSessionData(res?.event);
    } catch (err) {
      const errorMessage = err.error || 'Failed to fetch session Data';
      message.error(errorMessage);
    }
  };

  useEffect(() => {
    fetachById();
  }, [id, dispatch]);

  useEffect(() => {
    fetachRegiData(currentPage, 20);
  }, [currentPage, id]);

  const tableHeaders = [
    'Regi Id',
    'User Id',
    'Name',
    'Email',
    'Contact No',
    'Regi Date',
    'Action',
  ];

  const handleDelete = async (userId) => {
    if (!userId) {
      message.error('Something went wrong. Session Id not found');
      return;
    }

    try {
      await PrivateAxiosInstance.delete(
        URLs.DELETE_REGISTER_SESSION_URL.replace(':eventId', id),
        {
          data: { userId: userId },
        }
      );
      fetachRegiData();
      message.success('Delete successfully!');
    } catch (err) {
      message.error(err?.response?.data?.error || 'Something went wrong');
    }
  };

  const showConfirm = (userId) => {
    Modal.confirm({
      title: 'Confirm Delete',
      content: 'Are you sure you want to remove this user?',
      okText: 'Yes, Remove',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(userId);
      },
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const exportToExcel = () => {
    if (!id) return;

    if (!filteredData || filteredData.length === 0) {
      message.error('No data available to export');
      return;
    }

    // Preprocess data: remove undefined values and format fields as strings where needed
    const processedData = filteredData.map((item) => {
      const { _id, __v, registrationDate, ...rest } = item;
      return {
        ...Object.keys(rest).reduce((acc, key) => {
          acc[key] = rest[key] !== undefined ? String(rest[key]) : 'No data';
          return acc;
        }, {}),
        registrationDate: registrationDate
          ? new Date(registrationDate).toLocaleDateString()
          : 'No data',
      };
    });

    const settings = {
      fileName: `Registered-users-session-${id}`,
      extraLength: 3, // Optional: adjust column width
      writeOptions: {}, // Optional: xlsx-specific options
    };

    xlsx(
      [
        {
          sheet: `Session-${id}`,
          columns: Object.keys(processedData[0]).map((key) => ({
            label: key,
            value: key,
          })),
          content: processedData,
        },
      ],
      settings
    );
  };

  return (
    <>
      <div className="font-poppins overflow-x-auto mt-[70px]">
        <Spin spinning={loading} fullscreen />

        <div className="grid md:grid-cols-2 lg:grid-cols-4 md:gap-6 bg-background-secondary-color p-3 w-full rounded-t-lg">
          <h4 className="text-md ml-[20px] mt-3 font-poppins font-bold text-white">
            Session ID :
            <span className="text-md mt-3 font-normal text-white">
              {' '}
              {id || 'No data'}
            </span>
          </h4>

          <h4 className="text-md ml-[20px] mt-3 font-poppins font-bold text-white">
            Available Seats :
            <span className="text-md mt-3 font-normal text-white">
              {' '}
              {sessionData?.numOfSeats || 'No limit'}
            </span>
          </h4>

          <h4 className="text-md ml-[20px] mt-3 font-poppins font-bold text-white">
            Total Count :
            <span className="text-md mt-3 font-normal text-white">
              {' '}
              {count || 'No data'}
            </span>
          </h4>

          <div className="flex item-center justify-start mt-2 md:mt-0 ml-[20px]">
            <button
              onClick={exportToExcel}
              className="text-white px-2 rounded-lg bg-blue-500 hover:bg-blue-600 max-w-xl h-10"
            >
              Export to Excel
            </button>
          </div>
        </div>

        {/* Table headers */}
        <table className="min-w-full bg-white">
          <thead className="bg-gray-950 whitespace-nowrap">
            <tr>
              {tableHeaders.map((header, index) => (
                <th
                  key={index}
                  className="p-4 text-left text-sm font-medium font-poppins text-white"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>

          {/* Table row */}
          <tbody className="whitespace-nowrap">
            {filteredData.length > 0 ? (
              filteredData?.map((data, index) => (
                <tr
                  key={index}
                  className="bg-gray-800 odd:bg-gray-700 hover:bg-red-900"
                >
                  <td className="p-4 text-sm text-gray-200 font-poppins">
                    {data?.registerId || 'No data'}
                  </td>
                  <td className="p-4 text-sm text-gray-200 font-poppins">
                    {data?.userId || 'No data'}
                  </td>

                  <td className="p-4 text-sm text-gray-200 font-poppins">
                    {data?.name || 'No data'}
                  </td>

                  <td className="p-4 text-sm text-gray-200 font-poppins">
                    {data?.email || 'No data'}
                  </td>

                  <td className="p-4 text-sm text-gray-200 font-poppins">
                    {data?.phoneNumber || 'No data'}
                  </td>

                  <td className="p-4 text-sm text-gray-200 font-poppins">
                    {new Date(data?.registrationDate).toLocaleDateString() ||
                      'No data'}
                  </td>

                  <td>
                    <button
                      onClick={() => showConfirm(data?.userId)}
                      title="Delete"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-5 fill-red-500 hover:fill-red-700"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M19 7a1 1 0 0 0-1 1v11.191A1.92 1.92 0 0 1 15.99 21H8.01A1.92 1.92 0 0 1 6 19.191V8a1 1 0 0 0-2 0v11.191A3.918 3.918 0 0 0 8.01 23h7.98A3.918 3.918 0 0 0 20 19.191V8a1 1 0 0 0-1-1Zm1-3h-4V2a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v2H4a1 1 0 0 0 0 2h16a1 1 0 0 0 0-2ZM10 4V3h4v1Z"
                          data-original="#000000"
                        />
                        <path
                          d="M11 17v-7a1 1 0 0 0-2 0v7a1 1 0 0 0 2 0Zm4 0v-7a1 1 0 0 0-2 0v7a1 1 0 0 0 2 0Z"
                          data-original="#000000"
                        />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="text-center p-4">
                  No Data Available
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </>
      </div>
    </>
  );
}

export default Session;
